export type AspectRatio = 'landscape' | 'portrait' | 'square' | '16:9' | '9:16' | '1:1' | '4:5';

/**
 * Resolution tier for video output quality
 * Determines the pixel dimensions for a given aspect ratio
 */
export type ResolutionTier = '720p' | '1080p' | '1440p' | '4k';

/**
 * Resolution tier lookup table
 * Maps each tier to dimensions for all supported aspect ratios
 */
const RESOLUTION_TIER_MAP: Record<ResolutionTier, Record<AspectRatio, { width: number; height: number }>> = {
    '720p': {
        'landscape': { width: 1280, height: 720 },
        '16:9': { width: 1280, height: 720 },
        'portrait': { width: 720, height: 1280 },
        '9:16': { width: 720, height: 1280 },
        'square': { width: 720, height: 720 },
        '1:1': { width: 720, height: 720 },
        '4:5': { width: 720, height: 900 }
    },
    '1080p': {
        'landscape': { width: 1920, height: 1080 },
        '16:9': { width: 1920, height: 1080 },
        'portrait': { width: 1080, height: 1920 },
        '9:16': { width: 1080, height: 1920 },
        'square': { width: 1080, height: 1080 },
        '1:1': { width: 1080, height: 1080 },
        '4:5': { width: 1080, height: 1350 }
    },
    '1440p': {
        'landscape': { width: 2560, height: 1440 },
        '16:9': { width: 2560, height: 1440 },
        'portrait': { width: 1440, height: 2560 },
        '9:16': { width: 1440, height: 2560 },
        'square': { width: 1440, height: 1440 },
        '1:1': { width: 1440, height: 1440 },
        '4:5': { width: 1440, height: 1800 }
    },
    '4k': {
        'landscape': { width: 3840, height: 2160 },
        '16:9': { width: 3840, height: 2160 },
        'portrait': { width: 2160, height: 3840 },
        '9:16': { width: 2160, height: 3840 },
        'square': { width: 2160, height: 2160 },
        '1:1': { width: 2160, height: 2160 },
        '4:5': { width: 2160, height: 2700 }
    }
};

/**
 * Detect resolution tier from pixel dimensions
 * Uses maximum dimension to determine quality tier
 *
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns Detected resolution tier
 */
export function detectResolutionTier(width: number, height: number): ResolutionTier {
    const maxDimension = Math.max(width, height);

    if (maxDimension >= 2160) return '4k';
    if (maxDimension >= 1440) return '1440p';
    if (maxDimension >= 1080) return '1080p';
    return '720p';
}

/**
 * Detect aspect ratio from pixel dimensions
 * Matches to closest standard aspect ratio
 *
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns Detected aspect ratio
 */
export function detectAspectRatio(width: number, height: number): AspectRatio {
    const ratio = width / height;

    // Match to closest standard aspect ratio (with 10% tolerance)
    if (Math.abs(ratio - 16/9) < 0.1) return '16:9';
    if (Math.abs(ratio - 9/16) < 0.1) return '9:16';
    if (Math.abs(ratio - 1) < 0.1) return '1:1';
    if (Math.abs(ratio - 4/5) < 0.1) return '4:5';

    // Default based on orientation
    return ratio > 1 ? '16:9' : '9:16';
}

/**
 * Calculate video configuration (width/height) from aspect ratio and resolution tier
 * Uses standard dimensions based on quality tier and adjusts for aspect ratio
 *
 * @param aspectRatio - Target aspect ratio
 * @param resolutionTier - Target resolution quality (defaults to 1080p for backward compatibility)
 * @returns Video dimensions { width, height }
 */
export function getVideoConfigFromAspectRatio(
    aspectRatio: AspectRatio,
    resolutionTier: ResolutionTier = '1080p'
): { width: number; height: number } {
    return RESOLUTION_TIER_MAP[resolutionTier][aspectRatio];
}
